import { Flex } from 'antd';
import { PrimaryCard } from '../../../assets/styles/components/dashboard.styled';
import { Divider, Heading, Paragraph, Text } from '../../Ant';
import CardData from '../../dashboard/cardData';
import { Box, Coin, DollarCircle, EditDash, TaxReceipt, User, WalletMoney } from '../../icons';
import { IExpense } from '../../../interface/expense';
import { useTranslation } from 'react-i18next';
import { useTransactionParameters } from '../../../context/TransactionParameterContext';
import { SystemConstants } from '../../../constants/constants';
import { isEqual } from 'lodash';
import {
  borderColorMapping,
  expenseCostForwardTranslationMapping,
  expenseStatusTranslationMapping,
  tagColorMapping,
} from '../../../constants/expense';
import dayjs from 'dayjs';
import { formatter } from '../../../constants/utilityFunctions';
import { getTranslationFromTranslatableEntity } from '../../../constants/matter';
import { useLanguage } from '../../../context/LanguageContext';
import NoteIcons from '../../common/NoteIcons';
import SkeletonEntityCard from '../../common/skeleton/skeletonEntityCard';

const ExpenseDetailCard = ({
  expense,
  isLoading = false,
}: {
  expense: IExpense;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation();
  const { getAppLanguageCode } = useLanguage();
  const { parameters } = useTransactionParameters();
  const baseCurrency = parameters?.currency;
  const priceLabel = isEqual(expense?.unit?.id, SystemConstants.UNIT_HOUR)
    ? t('expense:hourPrice')
    : t('expense:unitPrice');
  if (isLoading) {
    return (
      <SkeletonEntityCard
        rows={[1, 2, 1, 1, 1, 1]}
        withSubcontentOnFirstRow
        className="py-10 px-10 h-full"
      />
    );
  }
  return (
    <PrimaryCard
      $isDetail
      className={`d-flex flex-col gap-10 h-full customer-card ${borderColorMapping[expense?.status]}`}
    >
      <Flex justify="space-between" align="center" className="w-full">
        <Flex gap={10} align="center">
          <Flex
            justify="center"
            align="center"
            className={`status-chip ${tagColorMapping[expense?.status]}`}
          >
            {t(expenseStatusTranslationMapping[expense?.status])}
          </Flex>
          <NoteIcons hasImportantNotes={expense?.hasImportantNotes} hasNotes={expense?.hasNotes} />
        </Flex>
        <Text $fontsize="13px" $color="title" $lineheight="20px" $width="auto ">
          {dayjs(expense?.expenseDate).format('MMM DD, YYYY')}
        </Text>
      </Flex>
      <Divider />
      <Flex vertical gap={10} className="flex-grow">
        <CardData
          icon={<Coin />}
          title={t('expense:expense')}
          content={expense?.subject ?? '-'}
          contentclass="fw-400 lineclamp-2"
        />
        <Flex justify="space-between">
          <CardData
            icon={<DollarCircle />}
            title={`${priceLabel} (${baseCurrency ?? 'EUR'})`}
            content={`${formatter.regionalNumberFormat({ value: expense?.unitPriceNet ?? 0, minimumFractionDigits: 2 })}`}
          />
          <CardData icon={<Box />} title={t('expense:units')} content={expense?.units ?? 0} />
        </Flex>

        <CardData
          icon={<WalletMoney />}
          title={t('expense:costForward')}
          content={t(expenseCostForwardTranslationMapping[expense?.costForward])}
        />
        <CardData
          icon={<TaxReceipt />}
          title={t('expense:taxFree')}
          content={expense?.taxFree ? t('common:yes') : t('common:no')}
        />

        <Divider />

        <CardData
          icon={<User />}
          title={t('common:author')}
          content={expense?.expenseUser?.fullName ?? '-'}
        />
        <CardData
          icon={<EditDash />}
          title={t('expense:expenseType')}
          content={getTranslationFromTranslatableEntity(
            expense?.expenseType?.translations,
            getAppLanguageCode(),
          )}
        />
        <Divider />
        <Flex justify="space-between" align="center">
          <Paragraph $color="title" $lineheight="20px" $fontsize="13px" $weight={500}>
            {`${t('expense:totalPrice')} (${baseCurrency ?? 'EUR'})`}
          </Paragraph>
          <Heading level={6} $color="title" $lineheight="20px" $fontsize="13px" $weight={700}>
            {formatter.regionalNumberFormat({
              value: expense?.totalPriceNet ?? 0,
              minimumFractionDigits: 2,
            })}
          </Heading>
        </Flex>
      </Flex>
    </PrimaryCard>
  );
};

export default ExpenseDetailCard;
